import { useState, useEffect, useMemo } from 'react'
import { CheckCircle2, XCircle, Edit2, Save, Loader2, ChevronDown, ChevronRight } from 'lucide-react'
import PageHeader from '../components/PageHeader'
import { apiService } from '../services/api'

interface Alert {
  alert_id: string
  user_id: string
  alert_priority: number
  action_recommendation: string
  alert_reason: string
  alert_type: string
  status: string
  review_status: string
  review_metadata?: {
    annotation?: string
    reviewer_notes?: string
    edits?: Record<string, any>
  }
  match: {
    account: { id: string; name: string }
    context: {
      need_id?: string
      engagement_ids?: string[]
      feature_source_ids?: string[]
    }
  }
  created_at: string
}

interface ReviewStats {
  pending_count: number
  approved_today: number
  rejected_today: number
  oldest_pending_age_hours: number | null
}

export default function Review() {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null)
  const [selectedAlertIds, setSelectedAlertIds] = useState<Set<string>>(new Set())
  const [stats, setStats] = useState<ReviewStats | null>(null)
  const [editing, setEditing] = useState<{ [key: string]: any }>({})
  const [expandedSourceData, setExpandedSourceData] = useState<Set<string>>(new Set())
  const [sourceData, setSourceData] = useState<{ [key: string]: any }>({})

  // Fetch pending alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true)
        const response = await apiService.alertReview.list({
          limit: 100,
          sort_by: 'priority'
        })
        if (response.data.success) {
          setAlerts(response.data.alerts || [])
          if (response.data.alerts?.length > 0 && !selectedAlertId) {
            setSelectedAlertId(response.data.alerts[0].alert_id)
          }
        }
      } catch (error: any) {
        console.error('Error fetching alerts:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAlerts()
    fetchStats()
  }, [])

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await apiService.alertReview.getStats()
      if (response.data.success) {
        setStats(response.data.stats)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  // Fetch source data for an alert
  const fetchSourceData = async (alertId: string) => {
    if (sourceData[alertId]) return // Already loaded

    try {
      const response = await apiService.alertReview.getSourceData(alertId)
      if (response.data.success) {
        setSourceData(prev => ({ ...prev, [alertId]: response.data.source_data }))
      }
    } catch (error) {
      console.error('Error fetching source data:', error)
    }
  }

  const selectedAlert = useMemo(() => {
    return alerts.find(a => a.alert_id === selectedAlertId) || null
  }, [alerts, selectedAlertId])

  // Update alert during review
  const handleUpdate = async (alertId: string) => {
    const edits = editing[alertId]
    if (!edits) return

    try {
      const response = await apiService.alertReview.update(alertId, edits)
      if (response.data.success) {
        // Refresh alerts
        const refreshResponse = await apiService.alertReview.list({ limit: 100, sort_by: 'priority' })
        if (refreshResponse.data.success) {
          setAlerts(refreshResponse.data.alerts || [])
        }
        setEditing(prev => {
          const next = { ...prev }
          delete next[alertId]
          return next
        })
      }
    } catch (error) {
      console.error('Error updating alert:', error)
    }
  }

  // Review single alert
  const handleReview = async (alertId: string, action: 'approve' | 'reject', annotation?: string, reviewerNotes?: string) => {
    try {
      const response = await apiService.alertReview.review(alertId, {
        action,
        annotation,
        reviewer_notes: reviewerNotes
      })
      if (response.data.success) {
        // Remove from list (it's no longer pending)
        setAlerts(prev => prev.filter(a => a.alert_id !== alertId))
        setSelectedAlertIds(prev => {
          const next = new Set(prev)
          next.delete(alertId)
          return next
        })
        if (selectedAlertId === alertId) {
          setSelectedAlertId(alerts.find(a => a.alert_id !== alertId)?.alert_id || null)
        }
        fetchStats()
      }
    } catch (error) {
      console.error('Error reviewing alert:', error)
    }
  }

  // Batch review
  const handleBatchReview = async (action: 'approve' | 'reject', annotation?: string) => {
    if (selectedAlertIds.size === 0) return

    try {
      const response = await apiService.alertReview.batchReview({
        alert_ids: Array.from(selectedAlertIds),
        action,
        annotation
      })
      if (response.data.success) {
        // Remove reviewed alerts from list
        setAlerts(prev => prev.filter(a => !selectedAlertIds.has(a.alert_id)))
        setSelectedAlertIds(new Set())
        fetchStats()
      }
    } catch (error) {
      console.error('Error batch reviewing:', error)
    }
  }

  const toggleSelect = (alertId: string) => {
    setSelectedAlertIds(prev => {
      const next = new Set(prev)
      if (next.has(alertId)) {
        next.delete(alertId)
      } else {
        next.add(alertId)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedAlertIds.size === alerts.length) {
      setSelectedAlertIds(new Set())
    } else {
      setSelectedAlertIds(new Set(alerts.map(a => a.alert_id)))
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Alert Review"
        subtitle="Review and approve alerts before they are sent to users"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-orange-600">{stats.pending_count}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Approved Today</div>
              <div className="text-2xl font-bold text-green-600">{stats.approved_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Rejected Today</div>
              <div className="text-2xl font-bold text-red-600">{stats.rejected_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Oldest Pending</div>
              <div className="text-2xl font-bold text-gray-600">
                {stats.oldest_pending_age_hours !== null ? `${stats.oldest_pending_age_hours}h` : 'N/A'}
              </div>
            </div>
          </div>
        )}

        {/* Batch Actions */}
        {selectedAlertIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="text-sm text-blue-800">
              {selectedAlertIds.size} alert{selectedAlertIds.size !== 1 ? 's' : ''} selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleBatchReview('approve')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                Approve Selected
              </button>
              <button
                onClick={() => handleBatchReview('reject')}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Reject Selected
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Alert List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold">Pending Alerts ({alerts.length})</h3>
                <button
                  onClick={toggleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  {selectedAlertIds.size === alerts.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {alerts.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No pending alerts
                  </div>
                ) : (
                  alerts.map(alert => (
                    <div
                      key={alert.alert_id}
                      className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                        selectedAlertId === alert.alert_id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => setSelectedAlertId(alert.alert_id)}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedAlertIds.has(alert.alert_id)}
                          onChange={(e) => {
                            e.stopPropagation()
                            toggleSelect(alert.alert_id)
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {alert.match.account.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Priority: {(alert.alert_priority * 100).toFixed(0)}%
                          </div>
                          <div className="text-xs text-gray-400 mt-1 truncate">
                            {alert.alert_reason}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Alert Detail */}
          <div className="lg:col-span-2">
            {selectedAlert ? (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">{selectedAlert.match.account.name}</h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleReview(selectedAlert.alert_id, 'approve')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleReview(selectedAlert.alert_id, 'reject')}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>

                  {/* Editable Fields */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Priority
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={editing[selectedAlert.alert_id]?.alert_priority ?? selectedAlert.alert_priority}
                        onChange={(e) => {
                          const alertId = selectedAlert.alert_id
                          setEditing(prev => ({
                            ...prev,
                            [alertId]: {
                              ...prev[alertId],
                              alert_priority: parseFloat(e.target.value)
                            }
                          }))
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Action Recommendation
                      </label>
                      <textarea
                        value={editing[selectedAlert.alert_id]?.action_recommendation ?? selectedAlert.action_recommendation}
                        onChange={(e) => {
                          const alertId = selectedAlert.alert_id
                          setEditing(prev => ({
                            ...prev,
                            [alertId]: {
                              ...prev[alertId],
                              action_recommendation: e.target.value
                            }
                          }))
                        }}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alert Reason
                      </label>
                      <textarea
                        value={editing[selectedAlert.alert_id]?.alert_reason ?? selectedAlert.alert_reason}
                        onChange={(e) => {
                          const alertId = selectedAlert.alert_id
                          setEditing(prev => ({
                            ...prev,
                            [alertId]: {
                              ...prev[alertId],
                              alert_reason: e.target.value
                            }
                          }))
                        }}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>

                    {editing[selectedAlert.alert_id] && (
                      <button
                        onClick={() => handleUpdate(selectedAlert.alert_id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Save Draft
                      </button>
                    )}
                  </div>

                  {/* Source Data */}
                  <div className="border-t border-gray-200 pt-4">
                    <button
                      onClick={() => {
                        const alertId = selectedAlert.alert_id
                        if (!expandedSourceData.has(alertId)) {
                          fetchSourceData(alertId)
                        }
                        setExpandedSourceData(prev => {
                          const next = new Set(prev)
                          if (next.has(alertId)) {
                            next.delete(alertId)
                          } else {
                            next.add(alertId)
                          }
                          return next
                        })
                      }}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                    >
                      {expandedSourceData.has(selectedAlert.alert_id) ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                      Source Data
                    </button>

                    {expandedSourceData.has(selectedAlert.alert_id) && sourceData[selectedAlert.alert_id] && (
                      <div className="mt-4 space-y-4 text-sm">
                        {sourceData[selectedAlert.alert_id].aggregated_need && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="font-medium mb-2">Aggregated Need</div>
                            <pre className="text-xs overflow-auto">
                              {JSON.stringify(sourceData[selectedAlert.alert_id].aggregated_need, null, 2)}
                            </pre>
                          </div>
                        )}
                        {sourceData[selectedAlert.alert_id].engagements?.length > 0 && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="font-medium mb-2">Engagements ({sourceData[selectedAlert.alert_id].engagements.length})</div>
                            <div className="text-xs text-gray-600">
                              {sourceData[selectedAlert.alert_id].engagements.slice(0, 3).map((e: any, i: number) => (
                                <div key={i} className="mb-1">{e.event_type || 'Engagement'}</div>
                              ))}
                            </div>
                          </div>
                        )}
                        {sourceData[selectedAlert.alert_id].opportunities && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="font-medium mb-2">Opportunities</div>
                            <div className="text-xs text-gray-600">
                              Open: {sourceData[selectedAlert.alert_id].opportunities.open_opportunities?.length || 0}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                Select an alert to review
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

